import { useEffect, useMemo, type ComponentProps } from 'react';
import type { Polygon, Position } from 'geojson';
import { debounce, type DebouncedFunc } from 'lodash-es';
import Map from 'components/map/map';
import { VisibleExtentEmitter } from './visible-extent-emitter';
import { DrawAoiControl } from './draw-aoi-control';
import { DrawPolygonsControl } from './draw-polygons-control';
import { FitBoundsControl, type BBox } from './fit-bounds-control';

export type { BBox } from './fit-bounds-control';

/** Viewport-derived query: reports the visible extent as one polygon on pan/zoom. */
export interface BoundsQuery {
  mode: 'bounds';
  /** Fires with the viewport polygon. Should be stable so the debounce isn't recreated each render. */
  onChange: (polygon: Polygon | null) => void;
  /** Optional debounce (ms) applied to `onChange`. */
  debounceMs?: number;
}

/**
 * Single-AOI query, panel-driven. The user draws ONE polygon directly — no toolbar
 * icons — and it is a controlled value: `polygon` is the source of truth, the drawing
 * reflects it, and changes (draw / vertex-edit / clear) flow out through `onChange`
 * (`null` on clear). The drawn polygon stays natively editable by clicking it on the
 * map; `editing` is a controlled, two-way flag for vertex editing (`direct_select`) so a
 * remote control (e.g. a left-panel "Edit") can initiate it and stay in sync.
 */
export interface SinglePolygonQuery {
  mode: 'polygon';
  /** Controlled selection — store it and echo `onChange` into it. `null` = nothing drawn. */
  polygon?: Polygon | null;
  /** Fires with the new polygon, or `null` when cleared. Stable so the debounce is stable. */
  onChange: (polygon: Polygon | null) => void;
  /**
   * Controlled vertex-editing flag. `true` puts the current polygon into `direct_select`;
   * `false` exits it. Mirror of the map's actual mode — combine with `onEditingChange`.
   */
  editing?: boolean;
  /**
   * Fires with the live editing state as the map's draw mode changes (entering
   * `direct_select` → `true`, leaving it → `false`) — so `editing` reflects native
   * click-to-edit and clears when the user clicks away. Stable so the listener is stable.
   */
  onEditingChange?: (editing: boolean) => void;
  /** Optional debounce (ms) applied to `onChange` (coalesces continuous vertex drags). */
  debounceMs?: number;
}

/**
 * Multi-AOI query, toolbar-driven. MapboxDraw's polygon button adds a polygon and the
 * trash button deletes the selected one; the whole set is controlled by `polygons` and
 * emitted via `onChange` on every change.
 */
export interface MultiPolygonQuery {
  mode: 'polygons';
  /** Controlled collection — store it and echo `onChange` into it. */
  polygons?: Polygon[];
  /** Fires with the full polygon set on every add / edit / delete. Stable so the debounce is stable. */
  onChange: (polygons: Polygon[]) => void;
  /**
   * Which MapboxDraw toolbar buttons to show (default: both). Pass
   * `{ polygon: false }` when a panel control arms drawing via `drawing`.
   */
  controls?: { polygon?: boolean; trash?: boolean };
  /**
   * Controlled flag that arms the next polygon, so a control outside the map
   * (e.g. a panel's "Draw polygon") can start a drawing. Mirror of the map's
   * actual mode — combine with `onDrawingChange`.
   */
  drawing?: boolean;
  /**
   * Fires with the live drawing state as the map's draw mode changes, so
   * `drawing` clears when the polygon is finished. Stable so the listener is.
   */
  onDrawingChange?: (drawing: boolean) => void;
  /** Index of the polygon to put into vertex editing, or null for none. */
  editingIndex?: number | null;
  /**
   * Fires with the index being edited as the map itself starts or ends an edit
   * (clicking a polygon edits it; clicking away stops), or null.
   */
  onEditingIndexChange?: (index: number | null) => void;
  /**
   * Corrects each vertex as it is placed — pass a snapper to pull points onto
   * nearby geometry, omit to take clicks as they land.
   */
  snapPosition?: (position: Position) => Position;
  /** Locks finished polygons until one is put into edit mode. See the control. */
  lockUnlessEditing?: boolean;
  /** Optional debounce (ms) applied to `onChange`. */
  debounceMs?: number;
}

export type SpatialQuery = BoundsQuery | SinglePolygonQuery | MultiPolygonQuery;

/** The emit signature varies by mode; `query.mode` discriminates it at each child site. */
type AnyOnChange =
  ((polygon: Polygon | null) => void) | ((polygons: Polygon[]) => void);

export type QueryMapProps = ComponentProps<typeof Map> & {
  /** Provide to turn the map into a spatial-query source. Omit for a plain map. */
  query?: SpatialQuery;
  /**
   * A collection of bounding boxes (`[minLng, minLat, maxLng, maxLat]`, the BE
   * `bbox` shape). When non-empty, the camera animates to frame the union of all
   * of them. Re-frames whenever the set changes; an empty/omitted collection
   * leaves the camera alone.
   */
  fitToBounds?: BBox[];
};

/**
 * Wrapper over the shared `<Map>` that can act as a spatial-query source. Three query
 * modes: `'bounds'` reports the visible viewport as a polygon on pan/zoom; `'polygon'`
 * lets the user draw/edit a single AOI directly (no toolbar — panel-driven); `'polygons'`
 * exposes MapboxDraw's polygon/trash toolbar to manage multiple AOIs. Each emits through
 * one `onChange`, debounced uniformly. With no `query` it is a straight passthrough.
 *
 * @param {QueryMapProps} props - All `<Map>` props plus the optional `query` bundle.
 * @return {JSX.Element} The composed `<Map>` element.
 */
export function QueryMap({
  query,
  fitToBounds,
  children,
  ...mapProps
}: QueryMapProps) {
  const onChange = query?.onChange as AnyOnChange | undefined;
  const debounceMs = query?.debounceMs;

  // The debounced wrapper, when a window is set — kept separately so its `.cancel()`
  // is typed (no cast) and the cleanup can target it directly.
  const debouncedEmit = useMemo<DebouncedFunc<AnyOnChange> | null>(
    () => (onChange && debounceMs ? debounce(onChange, debounceMs) : null),
    [onChange, debounceMs]
  );

  // The single emitter shared by both modes: the debounced wrapper when set,
  // otherwise the raw callback.
  const emit = debouncedEmit ?? onChange;

  // Cancel any pending debounced emit when it changes or on unmount.
  useEffect(() => () => debouncedEmit?.cancel(), [debouncedEmit]);

  return (
    <Map {...mapProps}>
      {children}
      {/* `query.mode` discriminates the emit signature, so the cast at each site is safe. */}
      {query?.mode === 'bounds' && emit && (
        <VisibleExtentEmitter onChange={emit as (p: Polygon | null) => void} />
      )}
      {query?.mode === 'polygon' && emit && (
        <DrawAoiControl
          polygon={query.polygon ?? null}
          onChange={emit as (p: Polygon | null) => void}
          editing={query.editing}
          onEditingChange={query.onEditingChange}
        />
      )}
      {query?.mode === 'polygons' && emit && (
        <DrawPolygonsControl
          polygons={query.polygons ?? []}
          onChange={emit as (p: Polygon[]) => void}
          controls={query.controls}
          drawing={query.drawing}
          onDrawingChange={query.onDrawingChange}
          editingIndex={query.editingIndex}
          onEditingIndexChange={query.onEditingIndexChange}
          snapPosition={query.snapPosition}
          lockUnlessEditing={query.lockUnlessEditing}
        />
      )}
      {fitToBounds && fitToBounds.length > 0 && (
        <FitBoundsControl bounds={fitToBounds} />
      )}
    </Map>
  );
}
